import { APP_INITIALIZER, enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { RouterSerializer } from './app/store/global/router.serializer';
import { NavigationActionTiming, StoreRouterConnectingModule } from '@ngrx/router-store';
import { CoreEffects } from './app/store/global/core-effects';
import { EffectsModule } from '@ngrx/effects';
import { metaReducers, reducers } from './app/store/global';
import { StoreModule } from '@ngrx/store';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideRouter, withRouterConfig } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication } from '@angular/platform-browser';
import { I18nInterceptor } from './app/core/interceptors/i18n.interceptor';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ShepherdService } from 'angular-shepherd';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { PaginatorI18n } from './app/core/paginator-i18n';
import { AuthenticationService } from './app/core/authentication.service';
import { LocalStorageKeys } from './app/components/locations-skeleton/enums/local-storage-keys';
import { AuthConfig, AuthHttpInterceptor, provideAuth0 } from '@auth0/auth0-angular';
import { getAuth0RedirectUri, getFromLocalStorage } from './app/core/utilities';
import { ScriptService } from './app/core/script.service';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { MAT_PAGINATOR_DEFAULT_OPTIONS, MatPaginatorIntl } from '@angular/material/paginator';
import { APP_ROUTES } from './app/app-routes';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import { NZ_DATE_CONFIG, NZ_DATE_LOCALE, NZ_I18N, NzI18nService } from 'ng-zorro-antd/i18n';
import { getAntdLocale } from './app/core/utils/antd-utils';
import { getDateFnsLocale, WEEK_STARTS_ON } from './app/core/utils/date-fns-utils';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MarkdownModule } from 'ngx-markdown';
import { LANGUAGES } from './app/core/constants';

if (environment.production) {
    enableProdMode();
}

registerLocaleData(localeEn);
registerLocaleData(localeDe);


// the redirect uri needs to be dynamic as we do named releases (like bls-xxxx-...)
const auth0Config: AuthConfig = environment.auth0;
// set locale so that the universal login components reflect user language
const defaultLanguage = 'en';
const cachedLanguage = getFromLocalStorage('LANGUAGE');

// set auth0config's redirect URI and locale
auth0Config.authorizationParams.redirect_uri = getAuth0RedirectUri();
auth0Config.authorizationParams.ui_locales = cachedLanguage ? cachedLanguage : defaultLanguage;


function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}


/**
 * Halts loading of the app until translations are loaded, so translate.instant() can be used reliably
 * Introduced in BLS-2832
 * Additional info: https://github.com/ngx-translate/core/issues/517
 * @param translate
 * @param scriptService
 */
function appInitializerFactory(translate: TranslateService, scriptService: ScriptService): () => Promise<any> {
    // Load script as soon as possible, no need to halt the app until it's loaded though
    scriptService.loadGtagScript();
    return () => new Promise(resolve => {
        translate.setDefaultLang('en');
      // Explicitly register all available languages here
      translate.addLangs(LANGUAGES.map(l => l.code));
        const language = getFromLocalStorage(LocalStorageKeys.LANGUAGE) || 'en';
        const langSet$ = translate.use(language);
        // subscription will be canceled when langSet$ completes
        langSet$.subscribe({
            next: () => console.info(`Successfully initialized '${language}' language.`),
            error: () => console.error(`Problem with '${language}' language initialization.`),
            complete: // continue loading the app
                () => resolve(null),
        });
    });
}


bootstrapApplication(AppComponent, {
    // app-wide, SINGLETON services don't have to be added here, if no additional config is required -
    // just make sure they're annotated with @Injectable({providedIn: 'root'}) which is generated
    // by default if you use the CLI
    providers: [
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: HttpLoaderFactory,
                    deps: [HttpClient],
                },
            }),
            StoreModule.forRoot(reducers, {
                metaReducers,
            }),
            EffectsModule.forRoot([CoreEffects]),
            StoreRouterConnectingModule.forRoot({
              serializer: RouterSerializer,
              // Stabilizes Router Store.
              // Router Store will update on navigatino end, not on  pending navigation.
              // For instance, if there is a canDeactivate guard, active route, params, etc.
              // will not update on pending navigation which is the default bahavior.
              navigationActionTiming: NavigationActionTiming.PostActivation,
            }),
            MarkdownModule.forRoot(),
        ),
        // ngZoneEventCoalescing: true yields a huge performance increase since we have
        // a lot of components in the dom tree in location list component
        // https://netbasal.com/reduce-change-detection-cycles-with-event-coalescing-in-angular-c4037199859f
        provideZoneChangeDetection({ eventCoalescing: true }),
        // provided on root lvl so that injecting it in components does not create another instance
        ShepherdService,
        NzI18nService,
        provideAuth0({
            ...auth0Config,
            httpInterceptor: {
                // match any request that starts with baseURL and add access token to it
              allowedList: [
                environment.baseURL + '/*',
                environment.baseAgentUrl + '/*',
              ],
            },
        }),
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideRouter(APP_ROUTES, withRouterConfig({ onSameUrlNavigation: 'reload' })),
        {
            provide: MatPaginatorIntl,
            deps: [TranslateService, AuthenticationService],
            useFactory: (translateService: TranslateService) => new PaginatorI18n(translateService),
        },
        {
            provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
            useValue: { formFieldAppearance: 'fill' },
        },
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                horizontalPosition: 'left',
                verticalPosition: 'bottom',
            },
        },
        {
            provide: NZ_I18N,
            useValue: getAntdLocale(getFromLocalStorage(LocalStorageKeys.LANGUAGE) || 'en'),
        },
        {
            // https://ng.ant.design/docs/i18n/en#how-to-format-a-date-using-date-fns
            provide: NZ_DATE_LOCALE,
            useValue: getDateFnsLocale(getFromLocalStorage(LocalStorageKeys.LANGUAGE) || 'en'),
        },
        {
            // Ensure Monday as the first day of the week as seen here:
            // https://github.com/NG-ZORRO/ng-zorro-antd/issues/2073#issuecomment-528380578
            // Note that this only applies to ANTD, and NOT to `date-fns`!
            // We handle date-fns with wrappers like `europeanStartOfWeek`, etc. which override
            // `weekStartsOn` each time.
            provide: NZ_DATE_CONFIG,
            useValue: {
                firstDayOfWeek: WEEK_STARTS_ON,
            },
        },
        // MAT date / time picker
        provideDateFnsAdapter(),
        {
            provide: MAT_DATE_LOCALE,
            useValue: getDateFnsLocale(getFromLocalStorage(LocalStorageKeys.LANGUAGE) || 'en'),
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [TranslateService, ScriptService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: I18nInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true,
        },

        // include providers dependant on environment (just store devtools for now)
    ].concat(environment.providers)
}).catch(err => console.error(err));
